// kon nimi · the journal's prompt banks. Five sections per word, each with a
// small bank of open prompts. Prompts are dealt one at a time per section
// (in a per-word rotation, so different words lead with different questions)
// and the page deepens as the learner returns. All free-text in phase 1;
// the len section trades its text box for the chip builder in a later phase.

import { WORD_PROMPTS } from "./tokiPonaWordPrompts";

// Deterministic tiny hash — used only to vary prompt order per word.
export function wordSeed(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h * 31 + str.charCodeAt(i)) >>> 0;
  }
  return h;
}

export const SECTIONS = [
  {
    id: "selo",
    name: "selo",
    label: "the edges",
    def: "where the word stretches, blurs, and finally stops",
  },
  {
    id: "len",
    name: "len",
    label: "the hats",
    def: "the word as noun, as verb, as modifier",
  },
  {
    id: "poka",
    name: "poka",
    label: "neighbors",
    def: "the words that live next door, and how they differ",
  },
  {
    id: "mi",
    name: "mi",
    label: "the anchor",
    def: "tying the word to your own life so it sticks",
  },
  {
    id: "musi",
    name: "musi",
    label: "play",
    def: "compounds, nonsense, and small poems",
  },
];

export const SECTION_IDS = SECTIONS.map((s) => s.id);

// {word} is replaced by the word under study.
export const PROMPTS = {
  selo: [
    {
      id: "selo-1",
      text: "What's the weirdest thing {word} could still honestly describe? Push it until it almost breaks, and write down where that point is.",
    },
    {
      id: "selo-2",
      text: "Name something that is almost-but-not-quite {word}. What exactly tips it over the line?",
    },
    {
      id: "selo-3",
      text: "Find a boundary case for {word} you genuinely can't decide on. Argue both sides in a sentence or two each.",
    },
    {
      id: "selo-4",
      text: "If {word} vanished from the language tomorrow, what would speakers reach for instead — and what shade of meaning would be lost forever?",
    },
  ],
  len: [
    {
      id: "len-1",
      text: "Write a sentence where {word} is a noun — the thing itself. Then say in a few words what the thing is.",
    },
    {
      id: "len-2",
      text: "Write a sentence where {word} does the work of a verb. What kind of doing is it — an action, a becoming, a state?",
    },
    {
      id: "len-3",
      text: "Put {word} after another word as a modifier. What does it do to the word it touches — narrow it, color it, turn it into something new?",
    },
  ],
  poka: [
    {
      id: "poka-1",
      text: "Which word feels closest to {word}? Describe where they overlap — and a situation where you would never swap one for the other.",
    },
    {
      id: "poka-2",
      text: "Does {word} have an opposite? If yes, is it a true mirror or a lopsided one? If no, why does nothing quite oppose it?",
    },
    {
      id: "poka-3",
      text: "Pick any other word you know and set it beside {word} as a compound. What would the pair mean? Would a stranger guess it?",
    },
  ],
  mi: [
    {
      id: "mi-1",
      text: "Something in your life right now is deeply {word}. What is it? Describe it so a stranger would understand why the word fits.",
    },
    {
      id: "mi-2",
      text: "Write a memory in which {word} was secretly the main character.",
    },
    {
      id: "mi-3",
      text: "Who is the most {word} person you know? What do they do that earns them the word?",
    },
  ],
  musi: [
    {
      id: "musi-1",
      text: "Invent a compound using {word} for something that has no name yet. Gloss it like a dictionary would.",
    },
    {
      id: "musi-2",
      text: "Write a tiny poem — two or three lines — where {word} carries all the weight.",
    },
    {
      id: "musi-3",
      text: "If {word} were a place you could visit, what would you find there? Who else would be around?",
    },
  ],
};

export const ALL_PROMPT_IDS = SECTION_IDS.flatMap((s) =>
  PROMPTS[s].map((p) => p.id)
);

export function promptById(id) {
  for (const s of SECTION_IDS) {
    const hit = PROMPTS[s].find((p) => p.id === id);
    if (hit) return hit;
  }
  return null;
}

export function formatPrompt(text, word) {
  return text.replaceAll("{word}", word);
}

// Bespoke edge questions written for this word specifically (selo section).
export function bespokePrompts(word) {
  return WORD_PROMPTS[word] || [];
}

// The bank for a section, rotated per word so two word pages rarely lead
// with the same question. The edges section deals this word's bespoke
// questions first — they're the good stuff — then the generic bank.
export function sectionPromptOrder(word, sectionId) {
  const bank = PROMPTS[sectionId] || [];
  const offset = bank.length ? wordSeed(`${word}·${sectionId}`) % bank.length : 0;
  const rotated = [...bank.slice(offset), ...bank.slice(0, offset)];
  if (sectionId === "selo") return [...bespokePrompts(word), ...rotated];
  return rotated;
}

// Look up a prompt's text for a given word — bespoke banks first, then the
// shared banks (used by the book view to caption saved entries).
export function promptForWord(word, promptId) {
  return (
    bespokePrompts(word).find((p) => p.id === promptId) || promptById(promptId)
  );
}

// The single open prompt for a section: the first unanswered one in this
// word's rotation, or null once the section's bank is exhausted.
export function nextPrompt(word, sectionId, wordEntries = {}) {
  return (
    sectionPromptOrder(word, sectionId).find((p) => !wordEntries[p.id]) || null
  );
}

// Already-answered prompts for a section, oldest answer first.
export function answeredPrompts(word, sectionId, wordEntries = {}) {
  return sectionPromptOrder(word, sectionId)
    .filter((p) => wordEntries[p.id])
    .sort((a, b) => (wordEntries[a.id].at || 0) - (wordEntries[b.id].at || 0));
}
